import { resolveApiBase } from "./apiBase";
import { apiGet, apiPost } from "./api";
import { getAuth } from "firebase/auth";

const BASE = resolveApiBase();

export type MerchantOnboardingPayload = {
  restaurantId?: string;
  businessName: string;
  legalName?: string;
  businessType?: "individual" | "proprietorship" | "partnership" | "private_limited" | "public_limited" | "llp" | "trust" | "society" | "ngo" | "not_yet_registered";
  ownerName: string;
  ownerEmail?: string;
  ownerPhone: string;
  ownerSameAsPrimaryContact?: boolean;
  primaryContactName?: string;
  primaryContactPhone?: string;
  primaryContactEmail?: string;
  primaryContactRole?: string;
  whatsappConsent?: boolean;
  city: string;
  pincode: string;
  address: string;
  latitude?: number;
  longitude?: number;
  geocodeStatus?: string;
  currentStep?: number;
  ecosystemCategory?: string;
  outletCategory?: string;
  cuisines?: string[];
  menuInputMode?: "menu_photos" | "manual_items" | "menu_url";
  listingMode?: "discovery_only" | "pickup_ordering" | "dining_visibility" | "restaurant_delivery";
  swiggyRestaurantId?: string;
  zomatoRestaurantId?: string;
  menuUrl?: string;
  menuPhotoUrls?: string[];
  menuItems?: Array<{
    id: string;
    name: string;
    price: number;
    category?: string;
    prepMins?: number;
    imageUrl?: string;
  }>;
  supportPhone?: string;
  operatingHours?: string;
  weeklyHours?: Array<{ day: "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun"; closed?: boolean; open?: string; close?: string }>;
  prepSlaMins?: number;
  supportsPickup?: boolean;
  supportsDelivery?: boolean;
  deliveryRadiusKm?: number;
  paymentUpiId?: string;
  paymentNotes?: string;
  payoutAccountName?: string;
  payoutUpiId?: string;
  payoutBankAccount?: string;
  payoutIfsc?: string;
  fssaiNumber?: string;
  gstApplicable?: boolean;
  gstin?: string;
  panNumber?: string;
  aadhaarLast4?: string;
  outletFrontPhotoUrl?: string;
  profilePhotoUrl?: string;
  foodPhotoUrls?: string[];
  fssaiDocUrl?: string;
  gstDocUrl?: string;
  panDocUrl?: string;
  bankProofUrl?: string;
  shopLicenseUrl?: string;
  cancelledChequeUrl?: string;
  agreementAccepted?: boolean;
  gatewayProvider?: "razorpay" | "phonepe" | "stripe" | "manual";
  gatewayMerchantId?: string;
  notes?: string;
};

export async function submitMerchantOnboarding(payload: MerchantOnboardingPayload) {
  const user = getAuth().currentUser;
  if (user) return apiPost("/merchant/onboarding/submit", payload);
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 20000);
  let res: Response;
  try {
    res = await fetch(`${BASE}/merchant/onboarding/public/submit`, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });
  } catch (err: any) {
    if (err?.name === "AbortError") {
      throw new Error("Restaurant submission timed out. Please retry; duplicate submissions are safely prevented.");
    }
    throw err;
  } finally {
    window.clearTimeout(timeout);
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) {
    throw new Error(String(data?.error || "Unable to submit restaurant onboarding"));
  }
  return data;
}

export type MerchantDocumentKind =
  | "menu_photo"
  | "fssai"
  | "gst"
  | "pan"
  | "bank_proof"
  | "shop_license"
  | "cancelled_cheque"
  | "outlet_front_photo"
  | "profile_photo"
  | "food_photo";

export async function precheckMerchantOnboardingLocation(payload: {
  businessName: string;
  city: string;
  pincode: string;
  address: string;
}): Promise<{
  duplicate: boolean;
  duplicateRestaurantId?: string | null;
  geocodeStatus?: string;
  geocodeSource?: string;
  geocodeError?: string | null;
  location?: { lat?: number | null; lng?: number | null; formattedAddress?: string | null };
}> {
  const res = await fetch(`${BASE}/merchant/onboarding/public/precheck`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) {
    throw new Error(String(data?.error || "Unable to validate this outlet yet"));
  }
  return {
    duplicate: Boolean(data?.duplicate),
    duplicateRestaurantId: data?.duplicateRestaurantId || null,
    geocodeStatus: data?.geocodeStatus,
    geocodeSource: data?.geocodeSource,
    geocodeError: data?.geocodeError || null,
    location: data?.location || {},
  };
}

export async function uploadMerchantDocument(payload: {
  kind: MerchantDocumentKind;
  ownerEmail?: string;
  ownerPhone: string;
  businessName: string;
  fileName: string;
  contentType: string;
  dataBase64: string;
}): Promise<{
  url: string;
  path: string;
  fileName: string;
  size: number;
  contentType: string;
  ocr?: {
    status?: string;
    text?: string;
    items?: Array<{ id: string; name: string; price: number; category?: string; prepMins?: number }>;
    error?: string;
  };
}> {
  let data: any;
  if (payload.kind === "menu_photo") {
    const response = await fetch(`${BASE}/merchant/onboarding/menu-photo/upload`, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    data = await response.json().catch(() => ({}));
    if (!response.ok || data?.ok === false) {
      throw new Error(String(data?.error || "Unable to upload menu photo"));
    }
  } else {
    data = await apiPost("/merchant/onboarding/documents/upload", payload);
  }
  return {
    url: String(data?.url || ""),
    path: String(data?.path || ""),
    fileName: String(data?.fileName || payload.fileName),
    size: Number(data?.size || 0),
    contentType: String(data?.contentType || payload.contentType),
    ocr: data?.ocr,
  };
}

export async function attachMerchantMenuPhotos(restaurantId: string, menuPhotoUrls: string[]) {
  const safeId = String(restaurantId || "").trim();
  const urls = Array.from(new Set(menuPhotoUrls.map((url) => String(url || "").trim()).filter(Boolean))).slice(0, 6);
  if (!safeId) throw new Error("Restaurant ID is required");
  if (!urls.length) throw new Error("At least one menu photo is required");
  return apiPost("/merchant/onboarding/menu-photos/attach", {
    restaurantId: safeId,
    menuPhotoUrls: urls,
  });
}

export async function fetchApprovedRestaurants(params?: {
  city?: string;
  pincode?: string;
  lat?: number;
  lng?: number;
}) {
  const q = new URLSearchParams();
  if (params?.city) q.set("city", params.city);
  if (params?.pincode) q.set("pincode", params.pincode);
  if (Number.isFinite(params?.lat)) q.set("lat", String(params?.lat));
  if (Number.isFinite(params?.lng)) q.set("lng", String(params?.lng));
  const suffix = q.toString() ? `?${q.toString()}` : "";
  const res = await fetch(`${BASE}/merchant/restaurants/public${suffix}`, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) {
    throw new Error(String(data?.error || "Unable to fetch restaurants"));
  }
  return Array.isArray(data?.restaurants) ? data.restaurants : [];
}

export async function fetchNearestOrderingRestaurant(location: { lat: number; lng: number }) {
  const query = new URLSearchParams({
    lat: String(location.lat),
    lng: String(location.lng),
  });
  const res = await fetch(`${BASE}/merchant/restaurants/nearest-ordering?${query.toString()}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) {
    throw new Error(String(data?.error || "Unable to find a nearby ordering restaurant"));
  }
  return data?.restaurant || null;
}

export async function fetchPublicRestaurantMenu(restaurantId: string) {
  const safeId = String(restaurantId || "").trim();
  if (!safeId) throw new Error("Restaurant ID is required");
  const res = await fetch(
    `${BASE}/merchant/restaurants/public/${encodeURIComponent(safeId)}/menu`,
    {
      method: "GET",
      headers: { Accept: "application/json" },
    },
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) {
    throw new Error(String(data?.error || "Unable to load restaurant menu"));
  }
  return data;
}

export type MerchantOrderStatus =
  | "placed"
  | "accepted"
  | "preparing"
  | "ready_for_pickup"
  | "picked_up"
  | "completed"
  | "rejected"
  | "cancelled";

export type MerchantOrderState = {
  id: string;
  orderId: string;
  ownerUid: string;
  merchantId?: string;
  platform?: string;
  restaurantId?: string;
  restaurantName?: string | null;
  dishName?: string | null;
  customerUid?: string | null;
  itemCount?: number | null;
  status: MerchantOrderStatus;
  isLive: boolean;
  pickupEtaMinutes?: number;
  pickupCode?: string;
  alertAcknowledgedAt?: string;
  pickupVerifiedAt?: string;
  amountTotal?: number;
  paymentMethod?: DirectPaymentMethod;
  paymentStatus?: string;
  paymentProvider?: string;
  paymentProviderOrderId?: string;
  paymentProviderPaymentId?: string;
  deliveryMode?: "pickup" | "delivery";
  customer?: {
    name?: string | null;
    phone?: string | null;
    address?: string | null;
  };
  items?: Array<{ id?: string; name?: string; qty?: number; price?: number }>;
  refundStatus?: string | null;
  refundAmount?: number | null;
  settlementStatus?: string | null;
  settlementId?: string | null;
  updatedAt?: string;
  createdAt?: string;
  timeline?: Array<{
    at: string;
    status: MerchantOrderStatus;
    by?: string | null;
    notes?: string | null;
  }>;
};

export async function fetchMerchantLiveOrders(limit = 40, restaurantId?: string): Promise<MerchantOrderState[]> {
  const query = new URLSearchParams({ limit: String(limit) });
  if (restaurantId) query.set("restaurantId", restaurantId);
  const data = await apiGet(`/merchant/orders/live?${query.toString()}`);
  return Array.isArray(data?.orders) ? data.orders : [];
}

export type MerchantSettlement = {
  id: string;
  restaurantId: string;
  status?: string;
  grossAmount?: number;
  bitewiseFee?: number;
  adjustments?: number;
  payoutAmount?: number;
  reference?: string | null;
  notes?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export async function fetchMerchantSettlements(restaurantId: string, limit = 50): Promise<MerchantSettlement[]> {
  const safeId = String(restaurantId || "").trim();
  if (!safeId) return [];
  const query = new URLSearchParams({ restaurantId: safeId, limit: String(limit) });
  const data = await apiGet(`/merchant/orders/settlements?${query.toString()}`);
  return Array.isArray(data?.settlements) ? data.settlements : [];
}

export type MerchantDailyReport = {
  restaurantId: string;
  date: string;
  timezone: string;
  summary: {
    orderCount: number;
    itemQuantity: number;
    grossSales: number;
    deliveryFees: number;
    packagingFees: number;
    discounts: number;
  };
  items: Array<{ id?: string; name: string; quantity: number; sales: number }>;
  orders: Array<{ orderId: string; status?: string; customerName?: string | null; amountTotal: number; paymentStatus?: string | null; placedAt?: string | null }>;
};

export async function fetchMerchantDailyReport(restaurantId: string, date?: string): Promise<MerchantDailyReport> {
  const safeId = String(restaurantId || "").trim();
  if (!safeId) throw new Error("restaurant id required");
  const query = new URLSearchParams({ restaurantId: safeId });
  if (date) query.set("date", date);
  const data = await apiGet(`/merchant/orders/reports/daily?${query.toString()}`);
  return {
    restaurantId: data?.restaurantId || safeId,
    date: data?.date || date || "",
    timezone: data?.timezone || "Asia/Kolkata",
    summary: data?.summary || {
      orderCount: 0,
      itemQuantity: 0,
      grossSales: 0,
      deliveryFees: 0,
      packagingFees: 0,
      discounts: 0,
    },
    items: Array.isArray(data?.items) ? data.items : [],
    orders: Array.isArray(data?.orders) ? data.orders : [],
  };
}

export async function acknowledgeMerchantOrderAlerts(orderIds: string[]) {
  const normalized = Array.from(new Set(orderIds.map((id) => String(id || "").trim()).filter(Boolean)));
  if (!normalized.length) return { ok: true, acknowledged: 0 };
  return apiPost("/merchant/orders/alerts/ack", { orderIds: normalized });
}

export async function fetchMerchantOrderDetail(id: string): Promise<{ order: MerchantOrderState; baseOrder?: any }> {
  const safeId = String(id || "").trim();
  if (!safeId) throw new Error("id required");
  const data = await apiGet(`/merchant/orders/${encodeURIComponent(safeId)}`);
  return {
    order: data?.order as MerchantOrderState,
    baseOrder: data?.baseOrder,
  };
}

export async function updateRestaurantAvailability(
  restaurantId: string,
  payload: {
    isOpen?: boolean;
    acceptsOrders?: boolean;
    supportsPickup?: boolean;
    supportsDelivery?: boolean;
    prepSlaMins?: number;
    pauseReason?: string;
  }
) {
  const safeId = String(restaurantId || "").trim();
  if (!safeId) throw new Error("restaurant id required");
  return apiPost(`/merchant/orders/restaurants/${encodeURIComponent(safeId)}/availability`, payload);
}

export async function updateRestaurantItemAvailability(
  restaurantId: string,
  payload: { itemId: string; name?: string; available: boolean; reason?: string }
) {
  const safeId = String(restaurantId || "").trim();
  if (!safeId) throw new Error("restaurant id required");
  return apiPost(`/merchant/orders/restaurants/${encodeURIComponent(safeId)}/items/availability`, payload);
}

export type MerchantEventKind =
  | "dashboard_view"
  | "quick_link_opened"
  | "menu_impression"
  | "menu_open"
  | "cart_build"
  | "offer_created"
  | "offer_status_changed"
  | "campaign_created"
  | "customer_message_sent"
  | "review_logged"
  | "complaint_logged";

export type MerchantOffer = {
  id: string;
  restaurantId: string;
  title: string;
  description?: string | null;
  discountType?: "percent" | "flat" | "freebie";
  discountValue?: number;
  status: "draft" | "active" | "paused" | "expired";
  startsAt?: string | null;
  endsAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

export type MerchantDashboardSummary = {
  restaurantId: string;
  days: number;
  totals: Record<string, number>;
  funnel: {
    impressions: number;
    menuOpens: number;
    cartBuilds: number;
    ordersPlaced: number;
    accepted: number;
    preparing: number;
    ready: number;
    completed: number;
    rejected: number;
  };
  sales: {
    gross: number;
    completedGross: number;
    averageOrderValue: number;
    uniqueCustomers: number;
  };
  quality: {
    complaints: number;
    reviews: number;
    customerMessages: number;
  };
  offers: MerchantOffer[];
  recentEvents: Array<Record<string, any>>;
};

export async function trackMerchantEvent(payload: {
  restaurantId?: string;
  kind: MerchantEventKind;
  source?: string;
  value?: number;
  payload?: Record<string, any>;
}) {
  return apiPost("/merchant/orders/events/track", payload);
}

export async function fetchMerchantDashboardSummary(params: { restaurantId?: string; days?: number }): Promise<MerchantDashboardSummary> {
  const q = new URLSearchParams();
  if (params.restaurantId) q.set("restaurantId", params.restaurantId);
  if (params.days) q.set("days", String(params.days));
  const data = await apiGet(`/merchant/orders/events/summary${q.toString() ? `?${q.toString()}` : ""}`);
  return {
    restaurantId: String(data?.restaurantId || params.restaurantId || ""),
    days: Number(data?.days || params.days || 7),
    totals: data?.totals || {},
    funnel: data?.funnel || {},
    sales: data?.sales || {},
    quality: data?.quality || {},
    offers: Array.isArray(data?.offers) ? data.offers : [],
    recentEvents: Array.isArray(data?.recentEvents) ? data.recentEvents : [],
  } as MerchantDashboardSummary;
}

export async function fetchMerchantOffers(restaurantId?: string): Promise<MerchantOffer[]> {
  const q = restaurantId ? `?restaurantId=${encodeURIComponent(restaurantId)}` : "";
  const data = await apiGet(`/merchant/orders/offers${q}`);
  return Array.isArray(data?.offers) ? data.offers : [];
}

export async function createMerchantOffer(payload: {
  restaurantId: string;
  title: string;
  description?: string;
  discountType?: "percent" | "flat" | "freebie";
  discountValue?: number;
  startsAt?: string;
  endsAt?: string;
  status?: "draft" | "active" | "paused";
}): Promise<MerchantOffer> {
  const data = await apiPost("/merchant/orders/offers", payload);
  return data?.offer as MerchantOffer;
}

export async function updateMerchantOfferStatus(id: string, status: "draft" | "active" | "paused" | "expired"): Promise<MerchantOffer> {
  const safeId = String(id || "").trim();
  if (!safeId) throw new Error("offer id required");
  const data = await apiPost(`/merchant/orders/offers/${encodeURIComponent(safeId)}/status`, { status });
  return data?.offer as MerchantOffer;
}

export async function merchantAcceptOrder(id: string, notes?: string) {
  const safeId = String(id || "").trim();
  if (!safeId) throw new Error("id required");
  return apiPost(`/merchant/orders/${encodeURIComponent(safeId)}/accept`, {
    ...(notes ? { notes } : {}),
  });
}

export async function merchantRejectOrder(id: string, notes?: string) {
  const safeId = String(id || "").trim();
  if (!safeId) throw new Error("id required");
  return apiPost(`/merchant/orders/${encodeURIComponent(safeId)}/reject`, {
    ...(notes ? { notes } : {}),
  });
}

export async function merchantUpdateOrderStatus(
  id: string,
  payload: {
    status: MerchantOrderStatus;
    notes?: string;
    pickupEtaMinutes?: number;
  }
) {
  const safeId = String(id || "").trim();
  if (!safeId) throw new Error("id required");
  return apiPost(`/merchant/orders/${encodeURIComponent(safeId)}/status`, payload);
}

export async function merchantVerifyPickupCode(id: string, code: string) {
  const safeId = String(id || "").trim();
  const safeCode = String(code || "").trim();
  if (!safeId) throw new Error("id required");
  if (!/^\d{4}$/.test(safeCode)) throw new Error("Enter the customer's four-digit pickup code");
  return apiPost(`/merchant/orders/${encodeURIComponent(safeId)}/pickup/verify`, { code: safeCode });
}

export type MerchantOnboardingRow = {
  id: string;
  restaurantId?: string;
  status: "pending" | "approved" | "rejected" | string;
  reviewState?: "draft" | "submitted" | "needs_changes" | "discovery_approved" | "ordering_approved" | "rejected" | string;
  businessName?: string;
  legalName?: string | null;
  ownerName?: string;
  ownerEmail?: string;
  ownerPhone?: string;
  ownerUid?: string | null;
  ownerSameAsPrimaryContact?: boolean;
  primaryContactName?: string | null;
  primaryContactPhone?: string | null;
  primaryContactEmail?: string | null;
  primaryContactRole?: string | null;
  whatsappConsent?: boolean;
  currentStep?: number | null;
  location?: {
    city?: string;
    pincode?: string;
    address?: string;
    lat?: number | null;
    lng?: number | null;
    geocodeStatus?: string | null;
    geocodeSource?: string | null;
  };
  cuisines?: string[];
  supportPhone?: string | null;
  menuInputMode?: "menu_photos" | "manual_items" | "menu_url" | string | null;
  listingMode?: "discovery_only" | "pickup_ordering" | "dining_visibility" | "restaurant_delivery" | string | null;
  operatingHours?: string | null;
  weeklyHours?: Array<{ day: string; closed?: boolean; open?: string; close?: string }>;
  prepSlaMins?: number | null;
  supportsPickup?: boolean;
  supportsDelivery?: boolean;
  deliveryRadiusKm?: number | null;
  paymentUpiId?: string | null;
  paymentNotes?: string | null;
  payoutAccountName?: string | null;
  payoutUpiId?: string | null;
  payoutBankAccount?: string | null;
  payoutIfsc?: string | null;
  platformIds?: { swiggy?: string | null; zomato?: string | null };
  kycStatus?: string;
  paymentGatewayStatus?: string;
  gatewayProvider?: string;
  gatewayMerchantId?: string | null;
  agreementAccepted?: boolean;
  fssaiNumber?: string | null;
  gstApplicable?: boolean;
  gstin?: string | null;
  panNumber?: string | null;
  aadhaarLast4?: string | null;
  outletFrontPhotoUrl?: string | null;
  profilePhotoUrl?: string | null;
  foodPhotoUrls?: string[];
  fssaiDocUrl?: string | null;
  gstDocUrl?: string | null;
  panDocUrl?: string | null;
  bankProofUrl?: string | null;
  shopLicenseUrl?: string | null;
  cancelledChequeUrl?: string | null;
  menuUrl?: string | null;
  menuPhotoUrls?: string[];
  menuItems?: Array<{ id?: string; name?: string; price?: number; category?: string; prepMins?: number }>;
  menuListingSource?: "manual" | "photos" | "url" | string;
  submittedAt?: string;
  updatedAt?: string;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  rejectionReason?: string | null;
  notes?: string | null;
  publicationMode?: "live" | "discovery_only" | string;
  discoveryOnly?: boolean;
  activationRequirementsMissing?: string[];
  activationReadiness?: { ok: boolean; missing: string[] };
  automation?: {
    stage?: string;
    completionPercent?: number;
    fssaiReviewStatus?: string;
    settlementStatus?: string;
    fieldChecks?: Record<string, string>;
    nextActions?: string[];
    activationReadiness?: { ok: boolean; missing: string[] };
  };
  kycVerification?: {
    status?: string;
    providerMode?: string;
    checkedAt?: string;
    checks?: Record<
      string,
      {
        status?: string;
        provider?: string | null;
        checkedAt?: string;
        message?: string | null;
        referenceId?: string | null;
      }
    >;
  };
};

async function adminHeaders() {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  const user = getAuth().currentUser;
  if (user) {
    const token = await user.getIdToken();
    if (token) headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

export async function fetchMerchantMe(): Promise<{
  ownerUid: string;
  approvedRestaurants: any[];
  onboarding: MerchantOnboardingRow[];
}> {
  const data = await apiGet("/merchant/orders/me");
  return {
    ownerUid: String(data?.ownerUid || ""),
    approvedRestaurants: Array.isArray(data?.approvedRestaurants) ? data.approvedRestaurants : [],
    onboarding: Array.isArray(data?.onboarding) ? data.onboarding : [],
  };
}

export async function fetchUserLiveMerchantOrders(limit = 20): Promise<MerchantOrderState[]> {
  const data = await apiGet(`/merchant/orders/user/live?limit=${encodeURIComponent(String(limit))}`);
  return Array.isArray(data?.orders) ? data.orders : [];
}

export async function fetchUserMerchantOrderDetail(id: string): Promise<{ order: MerchantOrderState; baseOrder?: any }> {
  const safeId = String(id || "").trim();
  if (!safeId) throw new Error("id required");
  const data = await apiGet(`/merchant/orders/user/${encodeURIComponent(safeId)}`);
  return {
    order: data?.order as MerchantOrderState,
    baseOrder: data?.baseOrder,
  };
}

export async function fetchUserMerchantOrderTrack(id: string): Promise<{
  order?: MerchantOrderState;
  focusedOrder?: MerchantOrderState;
  baseOrder?: any;
  parentOrder?: any;
  childOrders?: MerchantOrderState[];
}> {
  const safeId = String(id || "").trim();
  if (!safeId) throw new Error("id required");
  const data = await apiGet(`/merchant/orders/user/track/${encodeURIComponent(safeId)}`);
  return {
    order: data?.order as MerchantOrderState | undefined,
    focusedOrder: data?.focusedOrder as MerchantOrderState | undefined,
    baseOrder: data?.baseOrder,
    parentOrder: data?.parentOrder,
    childOrders: Array.isArray(data?.childOrders) ? data.childOrders : [],
  };
}

export async function fetchUserMerchantOrderHistory(limit = 50): Promise<MerchantOrderState[]> {
  const data = await apiGet(`/merchant/orders/user/history?limit=${encodeURIComponent(String(limit))}`);
  return Array.isArray(data?.orders) ? data.orders : [];
}

export async function cancelUserMerchantOrder(id: string, reason?: string) {
  const safeId = String(id || "").trim();
  if (!safeId) throw new Error("id required");
  return apiPost(`/merchant/orders/user/${encodeURIComponent(safeId)}/cancel`, {
    ...(reason ? { reason } : {}),
  });
}

export async function requestOrderSupport(id: string, payload: { issue?: string; contact?: string }) {
  const safeId = String(id || "").trim();
  if (!safeId) throw new Error("id required");
  return apiPost(`/merchant/orders/user/${encodeURIComponent(safeId)}/support`, payload);
}

export type DirectPaymentMethod = "cod" | "upi" | "card" | "wallet";

export async function createDirectRestaurantOrder(payload: {
  restaurantId: string;
  items: Array<{ id?: string; name: string; qty: number; price?: number; category?: string; addOns?: unknown[] }>;
  paymentMethod: DirectPaymentMethod;
  paymentProvider?: "razorpay" | "cashfree" | "stripe" | "phonepe" | "vendor_upi";
  deliveryMode?: "pickup" | "delivery";
  pickupMode?: "dine_in" | "takeaway";
  customerName?: string;
  customerPhone?: string;
  deliveryAddress?: string;
  deliveryInstructions?: string;
  deskIdentifier?: string;
  notes?: string;
  employeeCouponCode?: string;
  tableContext?: {
    restaurantId?: string;
    tableCode?: string;
    tableLabel?: string;
    section?: string;
    source?: string;
  };
  clientRequestId?: string;
}) {
  return apiPost("/merchant/direct-orders", payload);
}

export async function verifyDirectRestaurantPayment(
  orderId: string,
  payload: {
    provider?: "razorpay" | "cashfree" | "stripe" | "phonepe" | "cash";
    providerOrderId?: string;
    providerPaymentId?: string;
    providerSignature?: string;
    status?: "paid" | "failed" | "cancelled";
  }
) {
  const safeId = String(orderId || "").trim();
  if (!safeId) throw new Error("order id required");
  return apiPost(`/merchant/direct-orders/${encodeURIComponent(safeId)}/payment/verify`, payload);
}

export async function createFloorDeliveryPayment(
  orderId: string,
  payload: {
    deliveryAddress: string;
    deskIdentifier?: string;
  }
) {
  const safeId = String(orderId || "").trim();
  if (!safeId) throw new Error("order id required");
  return apiPost(`/merchant/direct-orders/${encodeURIComponent(safeId)}/floor-delivery/intent`, payload);
}

export async function verifyFloorDeliveryPayment(
  orderId: string,
  payload: {
    provider?: "cashfree";
    providerOrderId?: string;
    providerPaymentId?: string;
    status?: "paid" | "failed" | "cancelled";
  }
) {
  const safeId = String(orderId || "").trim();
  if (!safeId) throw new Error("order id required");
  return apiPost(`/merchant/direct-orders/${encodeURIComponent(safeId)}/floor-delivery/verify`, payload);
}

export async function claimRestaurantDashboard(restaurantId: string) {
  const safeId = String(restaurantId || "").trim();
  if (!safeId) throw new Error("restaurant id required");
  return apiPost("/merchant/restaurant-session/claim", { restaurantId: safeId });
}

export async function fetchAdminMerchantOnboarding(status: "pending" | "approved" | "rejected" | "all" = "pending") {
  const headers = await adminHeaders();
  const res = await fetch(`${BASE}/merchant/admin/onboarding?status=${encodeURIComponent(status)}`, {
    method: "GET",
    headers,
    credentials: "omit",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) throw new Error(String(data?.error || "Unable to load onboarding records"));
  return Array.isArray(data?.rows) ? (data.rows as MerchantOnboardingRow[]) : [];
}

export type AdminOperationsSnapshot = {
  summary: {
    liveOrders?: number;
    placed?: number;
    accepted?: number;
    preparing?: number;
    ready?: number;
    supportOpen?: number;
    notifications?: number;
    pendingOnboarding?: number;
  };
  liveOrders: MerchantOrderState[];
  orderEvents: any[];
  merchantEvents: any[];
  supportRequests: any[];
  onboardingRows: MerchantOnboardingRow[];
};

export async function fetchAdminOperations(limit = 80): Promise<AdminOperationsSnapshot> {
  const headers = await adminHeaders();
  const res = await fetch(`${BASE}/merchant/admin/operations?limit=${encodeURIComponent(String(limit))}`, {
    method: "GET",
    headers,
    credentials: "omit",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) throw new Error(String(data?.error || "Unable to load operations"));
  return {
    summary: data?.summary || {},
    liveOrders: Array.isArray(data?.liveOrders) ? data.liveOrders : [],
    orderEvents: Array.isArray(data?.orderEvents) ? data.orderEvents : [],
    merchantEvents: Array.isArray(data?.merchantEvents) ? data.merchantEvents : [],
    supportRequests: Array.isArray(data?.supportRequests) ? data.supportRequests : [],
    onboardingRows: Array.isArray(data?.onboardingRows) ? data.onboardingRows : [],
  };
}

export async function adminApproveMerchantOnboarding(
  id: string,
  payload?: { manualSettlement?: boolean; notes?: string }
) {
  const safeId = String(id || "").trim();
  if (!safeId) throw new Error("id required");
  const headers = await adminHeaders();
  const res = await fetch(`${BASE}/merchant/admin/onboarding/${encodeURIComponent(safeId)}/approve`, {
    method: "POST",
    headers,
    credentials: "omit",
    body: JSON.stringify({
      manualSettlement: payload?.manualSettlement === true,
      validationMode: payload?.manualSettlement === true ? "manual" : "external",
      notes: String(payload?.notes || "").trim() || undefined,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) throw new Error(String(data?.error || "Approval failed"));
  return data;
}

export async function adminApproveMerchantDiscovery(id: string) {
  const safeId = String(id || "").trim();
  if (!safeId) throw new Error("id required");
  const headers = await adminHeaders();
  const res = await fetch(`${BASE}/merchant/admin/onboarding/${encodeURIComponent(safeId)}/approve-discovery`, {
    method: "POST",
    headers,
    credentials: "omit",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) throw new Error(String(data?.error || "Discovery approval failed"));
  return data;
}

export async function adminRejectMerchantOnboarding(id: string, reason?: string) {
  const safeId = String(id || "").trim();
  if (!safeId) throw new Error("id required");
  const headers = await adminHeaders();
  const res = await fetch(`${BASE}/merchant/admin/onboarding/${encodeURIComponent(safeId)}/reject`, {
    method: "POST",
    headers,
    credentials: "omit",
    body: JSON.stringify({ reason: String(reason || "").trim() || undefined }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) throw new Error(String(data?.error || "Reject failed"));
  return data;
}

export async function adminUpdateMerchantKyc(
  id: string,
  payload: { status: "under_review" | "needs_changes" | "verified" | "rejected"; notes?: string; fields?: Record<string, boolean> }
) {
  const safeId = String(id || "").trim();
  if (!safeId) throw new Error("id required");
  const headers = await adminHeaders();
  const res = await fetch(`${BASE}/merchant/admin/onboarding/${encodeURIComponent(safeId)}/kyc`, {
    method: "POST",
    headers,
    credentials: "omit",
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) throw new Error(String(data?.error || "KYC update failed"));
  return data;
}

export async function adminEditMerchantOnboarding(
  id: string,
  payload: {
    reason: string;
    businessName?: string;
    legalName?: string;
    ownerName?: string;
    ownerEmail?: string;
    ownerPhone?: string;
    ownerSameAsPrimaryContact?: boolean;
    primaryContactName?: string;
    primaryContactPhone?: string;
    primaryContactEmail?: string;
    primaryContactRole?: string;
    whatsappConsent?: boolean;
    supportPhone?: string;
    city?: string;
    pincode?: string;
    address?: string;
    latitude?: number;
    longitude?: number;
    geocodeStatus?: string;
    currentStep?: number;
    ecosystemCategory?: string;
    outletCategory?: string;
    cuisines?: string[];
    menuInputMode?: "menu_photos" | "manual_items" | "menu_url";
    listingMode?: "discovery_only" | "pickup_ordering" | "dining_visibility" | "restaurant_delivery";
    menuUrl?: string | null;
    menuPhotoUrls?: string[];
    menuItems?: Array<{ id: string; name: string; price: number; category?: string; prepMins?: number; available?: boolean }>;
    operatingHours?: string;
    weeklyHours?: Array<{ day: "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun"; closed?: boolean; open?: string; close?: string }>;
    prepSlaMins?: number;
    supportsPickup?: boolean;
    supportsDelivery?: boolean;
    deliveryRadiusKm?: number | null;
    paymentUpiId?: string | null;
    paymentNotes?: string | null;
    payoutAccountName?: string | null;
    payoutUpiId?: string | null;
    payoutBankAccount?: string | null;
    payoutIfsc?: string | null;
    fssaiNumber?: string | null;
    gstApplicable?: boolean;
    gstin?: string | null;
    panNumber?: string | null;
    outletFrontPhotoUrl?: string | null;
    profilePhotoUrl?: string | null;
    foodPhotoUrls?: string[];
    fssaiDocUrl?: string | null;
    gstDocUrl?: string | null;
    panDocUrl?: string | null;
    bankProofUrl?: string | null;
    shopLicenseUrl?: string | null;
    cancelledChequeUrl?: string | null;
    agreementAccepted?: boolean;
    notes?: string | null;
  }
) {
  const safeId = String(id || "").trim();
  if (!safeId) throw new Error("id required");
  const headers = await adminHeaders();
  const res = await fetch(`${BASE}/merchant/admin/onboarding/${encodeURIComponent(safeId)}/edit`, {
    method: "POST",
    headers,
    credentials: "omit",
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) throw new Error(String(data?.error || "Restaurant update failed"));
  return data;
}

export async function adminResendMerchantAccess(id: string) {
  const safeId = String(id || "").trim();
  if (!safeId) throw new Error("id required");
  const headers = await adminHeaders();
  const res = await fetch(`${BASE}/merchant/admin/onboarding/${encodeURIComponent(safeId)}/resend-access`, {
    method: "POST",
    headers,
    credentials: "omit",
    body: JSON.stringify({}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) {
    const emailError = data?.emailDelivery?.error;
    const smsError = data?.smsDelivery?.error;
    throw new Error(String(data?.error || emailError || smsError || "Unable to resend restaurant access"));
  }
  return data;
}

export async function adminSelectMerchantMenuSource(
  id: string,
  payload: { source: "manual" | "photos" | "url"; notes?: string }
) {
  const safeId = String(id || "").trim();
  if (!safeId) throw new Error("id required");
  const headers = await adminHeaders();
  const res = await fetch(`${BASE}/merchant/admin/onboarding/${encodeURIComponent(safeId)}/menu-source`, {
    method: "POST",
    headers,
    credentials: "omit",
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) throw new Error(String(data?.error || "Menu source selection failed"));
  return data;
}

export async function adminDigitizeMerchantMenuPhotos(id: string) {
  const safeId = String(id || "").trim();
  if (!safeId) throw new Error("id required");
  const headers = await adminHeaders();
  const res = await fetch(`${BASE}/merchant/admin/onboarding/${encodeURIComponent(safeId)}/digitize-menu-photos`, {
    method: "POST",
    headers,
    credentials: "omit",
    body: JSON.stringify({}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) throw new Error(String(data?.error || "Menu digitization failed"));
  return data;
}

export async function adminGetMerchantEvidenceLink(id: string, field: string) {
  const safeId = String(id || "").trim();
  const safeField = String(field || "").trim();
  if (!safeId || !safeField) throw new Error("id and field are required");
  const headers = await adminHeaders();
  const res = await fetch(
    `${BASE}/merchant/admin/onboarding/${encodeURIComponent(safeId)}/evidence-link?field=${encodeURIComponent(safeField)}`,
    {
      method: "GET",
      headers,
      credentials: "omit",
    }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false || !data?.url) throw new Error(String(data?.error || "Evidence link failed"));
  return String(data.url);
}

export async function fetchAdminSettlements(params?: { restaurantId?: string; limit?: number }) {
  const headers = await adminHeaders();
  const q = new URLSearchParams();
  if (params?.restaurantId) q.set("restaurantId", params.restaurantId);
  if (params?.limit) q.set("limit", String(params.limit));
  const suffix = q.toString() ? `?${q.toString()}` : "";
  const res = await fetch(`${BASE}/merchant/admin/settlements${suffix}`, {
    method: "GET",
    headers,
    credentials: "omit",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) throw new Error(String(data?.error || "Unable to load settlements"));
  return Array.isArray(data?.rows) ? data.rows : [];
}

export async function adminCreateSettlement(payload: {
  restaurantId: string;
  orderIds?: string[];
  grossAmount?: number;
  bitewiseFee?: number;
  adjustments?: number;
  payoutAmount?: number;
  status?: "pending" | "processing" | "settled" | "failed" | "on_hold";
  reference?: string;
  notes?: string;
}) {
  const headers = await adminHeaders();
  const res = await fetch(`${BASE}/merchant/admin/settlements`, {
    method: "POST",
    headers,
    credentials: "omit",
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) throw new Error(String(data?.error || "Settlement create failed"));
  return data;
}

export async function adminUpdateSettlementStatus(
  id: string,
  payload: { status: "pending" | "processing" | "settled" | "failed" | "on_hold"; reference?: string; notes?: string }
) {
  const safeId = String(id || "").trim();
  if (!safeId) throw new Error("settlement id required");
  const headers = await adminHeaders();
  const res = await fetch(`${BASE}/merchant/admin/settlements/${encodeURIComponent(safeId)}/status`, {
    method: "POST",
    headers,
    credentials: "omit",
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) throw new Error(String(data?.error || "Settlement update failed"));
  return data;
}

export async function adminUpdateOrderRefund(
  id: string,
  payload: { status: "refund_pending" | "refund_processing" | "refunded" | "refund_failed"; amount?: number; reference?: string; reason?: string }
) {
  const safeId = String(id || "").trim();
  if (!safeId) throw new Error("id required");
  const headers = await adminHeaders();
  const res = await fetch(`${BASE}/merchant/admin/orders/${encodeURIComponent(safeId)}/refund`, {
    method: "POST",
    headers,
    credentials: "omit",
    body: JSON.stringify(payload),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data?.ok === false) throw new Error(String(data?.error || "Refund update failed"));
  return data;
}
